import { createBrowserRouter, Navigate } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import ProtectedRoute from '@/components/ProtectedRoute'
import RootLayout from '@/components/RootLayout'

// Lazy load page components for code splitting
const HomePage = lazy(() => import('@/pages/HomePage'))
const LoginPage = lazy(() => import('@/pages/LoginPage'))
const ProjectsPage = lazy(() => import('@/pages/ProjectsPage'))
const ProjectDetailPage = lazy(() => import('@/pages/ProjectDetailPage'))
const ProjectStepPage = lazy(() => import('@/pages/ProjectStepPage'))
const NewProjectPage = lazy(() => import('@/pages/NewProjectPage'))
const ClientsPage = lazy(() => import('@/pages/ClientsPage'))
const ClientDetailPage = lazy(() => import('@/pages/ClientDetailPage'))
const NewClientPage = lazy(() => import('@/pages/NewClientPage'))
const EditClientPage = lazy(() => import('@/pages/EditClientPage'))
const SettingsPage = lazy(() => import('@/pages/SettingsPage'))
const ProfileSettingsPage = lazy(() => import('@/pages/ProfileSettingsPage'))
const UserManagementPage = lazy(() => import('@/pages/UserManagementPage'))
const DocumentsPage = lazy(() => import('@/pages/DocumentsPage'))
const PromptsPage = lazy(() => import('@/pages/PromptsPage'))
const PromptDetailPage = lazy(() => import('@/pages/PromptDetailPage'))
const DocsPage = lazy(() => import('@/pages/DocsPage'))
const IssuesPage = lazy(() => import('@/pages/IssuesPage'))
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'))
const UpdatePasswordPage = lazy(() => import('@/pages/UpdatePasswordPage'))

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-gray-50 flex items-center justify-center">
    <LoadingSpinner />
  </div>
)

// Wrapper for lazy-loaded components
const LazyWrapper = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PageLoader />}>{children}</Suspense>
)

export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    children: [
      {
        index: true,
        element: (
          <LazyWrapper>
            <ProtectedRoute>
              <HomePage />
            </ProtectedRoute>
          </LazyWrapper>
        ),
      },
      {
        path: 'login',
        element: (
          <LazyWrapper>
            <LoginPage />
          </LazyWrapper>
        ),
      },
      {
        path: 'reset-password',
        element: (
          <LazyWrapper>
            <ResetPasswordPage />
          </LazyWrapper>
        ),
      },
      {
        path: 'update-password',
        element: (
          <LazyWrapper>
            <UpdatePasswordPage />
          </LazyWrapper>
        ),
      },
      {
        path: 'projects',
        element: (
          <LazyWrapper>
            <ProtectedRoute>
              <ProjectsPage />
            </ProtectedRoute>
          </LazyWrapper>
        ),
      },
      {
        path: 'projects/new',
        element: (
          <LazyWrapper>
            <ProtectedRoute>
              <NewProjectPage />
            </ProtectedRoute>
          </LazyWrapper>
        ),
      },
      {
        path: 'project/:projectId',
        element: (
          <LazyWrapper>
            <ProtectedRoute>
              <ProjectDetailPage />
            </ProtectedRoute>
          </LazyWrapper>
        ),
      },
      {
        path: 'project/:projectId/settings',
        element: (
          <LazyWrapper>
            <ProtectedRoute>
              <ProjectDetailPage />
            </ProtectedRoute>
          </LazyWrapper>
        ),
      },
      {
        path: 'project/:projectId/step/:stepNum',
        element: (
          <LazyWrapper>
            <ProtectedRoute>
              <ProjectStepPage />
            </ProtectedRoute>
          </LazyWrapper>
        ),
      },
      {
        path: 'clients',
        element: (
          <LazyWrapper>
            <ProtectedRoute>
              <ClientsPage />
            </ProtectedRoute>
          </LazyWrapper>
        ),
      },
      {
        path: 'clients/new',
        element: (
          <LazyWrapper>
            <ProtectedRoute>
              <NewClientPage />
            </ProtectedRoute>
          </LazyWrapper>
        ),
      },
      {
        path: 'client/:clientId',
        element: (
          <LazyWrapper>
            <ProtectedRoute>
              <ClientDetailPage />
            </ProtectedRoute>
          </LazyWrapper>
        ),
      },
      {
        path: 'client/:clientId/edit',
        element: (
          <LazyWrapper>
            <ProtectedRoute>
              <EditClientPage />
            </ProtectedRoute>
          </LazyWrapper>
        ),
      },
      {
        path: 'documents',
        element: (
          <LazyWrapper>
            <ProtectedRoute>
              <DocumentsPage />
            </ProtectedRoute>
          </LazyWrapper>
        ),
      },
      {
        path: 'prompts',
        element: (
          <LazyWrapper>
            <ProtectedRoute>
              <PromptsPage />
            </ProtectedRoute>
          </LazyWrapper>
        ),
      },
      {
        path: 'prompts/:promptKey',
        element: (
          <LazyWrapper>
            <ProtectedRoute>
              <PromptDetailPage />
            </ProtectedRoute>
          </LazyWrapper>
        ),
      },
      {
        path: 'docs',
        element: (
          <LazyWrapper>
            <ProtectedRoute>
              <DocsPage />
            </ProtectedRoute>
          </LazyWrapper>
        ),
      },
      {
        path: 'settings',
        element: (
          <LazyWrapper>
            <ProtectedRoute>
              <SettingsPage />
            </ProtectedRoute>
          </LazyWrapper>
        ),
      },
      {
        path: 'profile',
        element: (
          <LazyWrapper>
            <ProtectedRoute>
              <ProfileSettingsPage />
            </ProtectedRoute>
          </LazyWrapper>
        ),
      },
      {
        path: 'settings/profile',
        element: <Navigate to="/profile" replace />,
      },
      {
        path: 'users',
        element: (
          <LazyWrapper>
            <ProtectedRoute>
              <UserManagementPage />
            </ProtectedRoute>
          </LazyWrapper>
        ),
      },
      {
        path: 'settings/users',
        element: <Navigate to="/users" replace />,
      },
      {
        path: '*',
        element: <Navigate to="/projects" replace />,
      },
      {
        path: 'issues',
        element: (
          <LazyWrapper>
            <ProtectedRoute>
              <IssuesPage />
            </ProtectedRoute>
          </LazyWrapper>
        ),
      },
    ],
  },
])

